import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useOrganization } from '../../../contexts/OrganizationContext';
import {
    type OnboardingStepId,
    type OnboardingProgressInput,
    getCurrentOnboardingStep,
    isOnboardingComplete,
    isWhatsAppStepDone,
    isFunnelStepDone,
} from '../domain/onboardingProgress';

const ONBOARDING_UPDATED_EVENT = 'crm:onboarding-updated';

type OrgSettings = Record<string, unknown> & {
    onboarding?: {
        funnelCustomized?: boolean;
        whatsappSkipped?: boolean;
        funnelSkipped?: boolean;
    };
};

function readOnboardingSettings(settings: unknown) {
    const onboarding = (settings as OrgSettings | null)?.onboarding;
    return {
        funnelCustomized: onboarding?.funnelCustomized === true,
        whatsappSkipped: onboarding?.whatsappSkipped === true,
        funnelSkipped: onboarding?.funnelSkipped === true,
    };
}

/** Marca etapa de funil do onboarding como concluída após edição salva. */
export async function markFunnelCustomized(organizationId: string): Promise<boolean> {
    const { data: org, error: fetchError } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', organizationId)
        .single();

    if (fetchError || !org) {
        console.error('[Onboarding] Failed to load organization settings:', fetchError);
        return false;
    }

    const current = (org.settings as OrgSettings | null) ?? {};
    if (current.onboarding?.funnelCustomized === true) {
        window.dispatchEvent(new CustomEvent(ONBOARDING_UPDATED_EVENT));
        return true;
    }

    // .select() detects RLS no-op (0 rows, no error) — common for member/viewer
    const { data, error: updateError } = await supabase
        .from('organizations')
        .update({
            settings: {
                ...current,
                onboarding: { ...current.onboarding, funnelCustomized: true },
            },
        })
        .eq('id', organizationId)
        .select('id')
        .maybeSingle();

    if (updateError || !data) {
        console.error('[Onboarding] Failed to mark funnel as customized:', updateError ?? 'no row updated (RLS?)');
        return false;
    }

    window.dispatchEvent(new CustomEvent(ONBOARDING_UPDATED_EVENT));
    return true;
}

export interface OnboardingSteps {
    whatsappConnected: boolean;
    funnelCustomized: boolean;
    whatsappSkipped: boolean;
    funnelSkipped: boolean;
}

export function useOnboardingProgress() {
    const { user } = useAuth();
    const { currentOrganization, isLoading: orgLoading } = useOrganization();
    const orgId = currentOrganization?.id;
    const [steps, setSteps] = useState<OnboardingSteps>({
        whatsappConnected: false,
        funnelCustomized: false,
        whatsappSkipped: false,
        funnelSkipped: false,
    });
    const [currentStep, setCurrentStep] = useState<OnboardingStepId | null>(1);
    const [isLoading, setIsLoading] = useState(true);
    const [hasResolvedProgress, setHasResolvedProgress] = useState(false);
    const resolvedOrgIdRef = useRef<string | null>(null);
    const hasResolvedRef = useRef(false);

    // Troca de org: volta a exigir loading inicial (sem apagar UI no refetch da mesma org).
    useEffect(() => {
        if (orgId === resolvedOrgIdRef.current) return;
        hasResolvedRef.current = false;
        setHasResolvedProgress(false);
        setIsLoading(true);
    }, [orgId]);

    const checkProgress = useCallback(async () => {
        if (!user?.id) {
            hasResolvedRef.current = true;
            resolvedOrgIdRef.current = null;
            setHasResolvedProgress(true);
            setIsLoading(false);
            return;
        }

        // Wait for OrganizationContext — marking incomplete before org/progress
        // loads forces /onboarding then dashboard on every reload.
        if (orgLoading) {
            if (!hasResolvedRef.current) setIsLoading(true);
            return;
        }

        if (!orgId) {
            hasResolvedRef.current = true;
            resolvedOrgIdRef.current = null;
            setHasResolvedProgress(true);
            setIsLoading(false);
            return;
        }

        const isInitial = !hasResolvedRef.current || resolvedOrgIdRef.current !== orgId;
        if (isInitial) setIsLoading(true);

        try {
            const { count: wpCount } = await supabase
                .from('integrations')
                .select('id', { count: 'exact', head: true })
                .eq('organization_id', orgId)
                .eq('type', 'whatsapp');

            const { data: orgData } = await supabase
                .from('organizations')
                .select('settings')
                .eq('id', orgId)
                .single();

            const onboardingSettings = readOnboardingSettings(orgData?.settings);

            const progressInput: OnboardingProgressInput = {
                whatsappConnected: (wpCount || 0) > 0,
                funnelCustomized: onboardingSettings.funnelCustomized,
                whatsappSkipped: onboardingSettings.whatsappSkipped,
                funnelSkipped: onboardingSettings.funnelSkipped,
            };

            setSteps(progressInput);
            setCurrentStep(getCurrentOnboardingStep(progressInput));
            resolvedOrgIdRef.current = orgId;
        } catch (error) {
            console.error('[Onboarding] Error checking progress:', error);
        } finally {
            hasResolvedRef.current = true;
            setHasResolvedProgress(true);
            setIsLoading(false);
        }
    }, [user?.id, orgId, orgLoading]);

    useEffect(() => {
        checkProgress();
    }, [checkProgress]);

    useEffect(() => {
        const onRefresh = () => { void checkProgress(); };
        window.addEventListener('crm:billing-updated', onRefresh);
        window.addEventListener('crm:onboarding-updated', onRefresh);
        return () => {
            window.removeEventListener('crm:billing-updated', onRefresh);
            window.removeEventListener('crm:onboarding-updated', onRefresh);
        };
    }, [checkProgress]);

    // Only owner/admin can UPDATE organizations.settings (RLS). Members/viewers
    // must not be trapped in the wizard — org setup is not their responsibility.
    const canManageOnboarding =
        currentOrganization?.role === 'owner' || currentOrganization?.role === 'admin';

    const progressInput: OnboardingProgressInput = steps;
    // Until progress for this org is resolved, treat as loading (not incomplete).
    const isCompleted = !canManageOnboarding || isOnboardingComplete(progressInput);
    const resolvedStep = canManageOnboarding ? currentStep : null;
    const progressValue =
        (isWhatsAppStepDone(progressInput) ? 1 : 0) +
        (isFunnelStepDone(progressInput) ? 1 : 0);
    const totalValue = 2;
    // Spinner só no carregamento inicial — refetch entre etapas não desmonta a UI.
    const showInitialLoading =
        isLoading ||
        (!hasResolvedProgress && (orgLoading || (Boolean(user?.id) && Boolean(orgId))));

    return {
        steps,
        currentStep: resolvedStep,
        isLoading: showInitialLoading,
        progressValue,
        totalValue,
        percentage: Math.round((progressValue / totalValue) * 100),
        isCompleted,
        refetch: checkProgress,
    };
}
